// ================================================================ //
// -- Functional Components (exclusive to fluid-component-template)
//
// NB: If functional component has a use outside of this component,
// it should be a common component in common.components.tsx

import {
  FluidColor,
  FluidIconKey,
  FluidSize,
  FluidTheme,
} from '@lmig/fluid-core';
import { h } from '@stencil/core';

import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';

export interface FluidImageTranslationOptions {
  src?: string;
  altText?: string;
}

export interface FluidImageConfig {
  src: string;
  width?: number;
  noContainer?: boolean;
  altText?: string;
  enableZoomModal?: boolean;
  theme?: FluidTheme;
  overrideGlobalTheme?: boolean;
  fluidTranslate?: FluidImageTranslationOptions;
}

/**
 * Renders the actual image element, with whatever alt-text and source is provided.
 * @param props: { imageError: boolean, altText: string, setImageRef: Function, src: string }
 * @constructor
 */
interface ImageElementProps {
  imageError?: boolean;
  altText?: string;
  setImageRef: Function;
  src?: string;
  fluidTranslate?: FluidImageTranslationOptions;
  i18nStore?: FluidI18nStore;
}
export const ImageElement = (props: ImageElementProps) => {
  const { src, imageError, altText, setImageRef, fluidTranslate, i18nStore } =
    props;
  const imageSrc = i18nStore?.translate(fluidTranslate?.src) || src;
  const imageAltText = i18nStore?.translate(fluidTranslate?.altText) || altText;

  const imageClass = {
    image: true,
    hidden: imageError,
  };

  const aria = {
    alt: imageAltText,
    role: !altText && 'presentation',
  };

  return (
    <img
      src={imageSrc}
      class={imageClass}
      ref={(el) => setImageRef(el)}
      {...aria}
    />
  );
};

/**
 * Renders an error container, with image icon and error message rather than a broken
 * image link.
 * @param props: { imageError: boolean }
 * @constructor
 */
export const ImageError = (props: { imageError: boolean }) => {
  const { imageError } = props;
  const errorIcon = {
    key: FluidIconKey.IMAGE,
    size: FluidSize.LARGE,
    color: FluidColor.LIGHT_GRAY,
    customStyles: {
      marginBottom: 5,
    },
    interactive: false,
  };

  return (
    !!imageError && (
      <div class={'image-error'}>
        <fluid-icon config={errorIcon} />
        <fluid-text muted color={FluidColor.LIGHT_GRAY} disableGutters={true}>
          Image Failed to Load
        </fluid-text>
      </div>
    )
  );
};

export const ImageZoom = (props: {
  enabled: boolean;
  src: string;
  zoomOpen: boolean;
  toggleZoom: Function;
}) => {
  const { enabled, src, zoomOpen, toggleZoom } = props;
  const iconConfig = {
    key: FluidIconKey.SEARCH,
    size: FluidSize.EXTRA_SMALL,
    color: FluidColor.OPAQUE,
  };
  return (
    enabled && [
      <span class={'zoom-icon'} onClick={() => toggleZoom(true)}>
        <fluid-icon config={iconConfig} />
      </span>,
      <fluid-modal
        open={zoomOpen}
        size={FluidSize.MEDIUM}
        onModalClosed={() => toggleZoom(false)}>
        <img src={src} style={{ width: '100%' }} />
      </fluid-modal>,
    ]
  );
};

/**
 * Renders a loading spinner container, which when imageLoading is true, renders a loading
 * spinner over the image until it reports that it is loaded.
 * @param props: { theme: FluidTheme, noContainer: boolean, width: number, imageLoading: boolean}
 * @param children
 * @constructor
 */
export const ImageContainer = (
  props: {
    theme: FluidTheme;
    noContainer: boolean;
    width: number;
    imageLoading: boolean;
    onHoverState: Function;
  },
  children
) => {
  const { theme, noContainer, width, imageLoading, onHoverState } = props;
  const componentWrapper = {
    'fluid-image': true,
    [`theme-${theme}`]: true,
    'no-container': noContainer,
  };

  const imageStyle = width ? { 'max-width': `${width}px` } : {};
  const interactionState = {
    onMouseEnter: () => onHoverState(true),
    onMouseLeave: () => onHoverState(false),
  };
  return (
    <fluid-loading-spinner container={true} loading={imageLoading} muted={true}>
      <div class={componentWrapper} style={imageStyle} {...interactionState}>
        {children}
      </div>
    </fluid-loading-spinner>
  );
};
